import React, { useState, useCallback, useMemo } from 'react';
import { Objective, Milestone, CreateObjectiveInput, CreateMilestoneInput, TrackBy, TRACK_BY_LABELS } from '../../types/pms';
import { validateCreateObjectiveInput } from '../../types/pmsValidation';
import ProgressSlider, { clampProgress } from './ProgressSlider';
import './ObjectiveForm.css';

interface InlineMilestone {
  title: string;
  description: string;
  dueDate: string;
  weightage: string;
}

interface ObjectiveFormProps {
  objective?: Objective;
  existingWeightageTotal?: number;
  /** Pre-existing milestones for editing */
  existingMilestones?: Milestone[];
  /** Save goal (always as draft). Submit happens in bulk from the dashboard. */
  /**
   * May return a promise, and may reject: a rejection means the goal was not saved and is
   * rendered on the form rather than swallowed, so the typed-in goal is still there to retry.
   */
  onSave: (input: CreateObjectiveInput, managerId: string, managerName: string, milestones: CreateMilestoneInput[]) => void | Promise<void>;
  onCancel: () => void;
  reviewCycleId?: string;
  loading?: boolean;
  /** Manager is auto-assigned from user management data */
  defaultManagerId?: string;
  /**
   * The manager's display name, from the same User Management record as defaultManagerId.
   *
   * handleSave used to pass a literal '' for the name, so createObjective stored its
   * `managerName || 'Unassigned'` placeholder on every objective — even when the id beside it
   * was correct. That placeholder was then copied onto the review at self-review submission and
   * rendered as the word "Unassigned" next to the Manager label. Resolving the name where the id
   * is already resolved stops the placeholder being written in the first place.
   */
  defaultManagerName?: string;
}

interface FieldErrors {
  title?: string;
  description?: string;
  measureOfSuccess?: string;
  startDate?: string;
  endDate?: string;
  weightage?: string;
  employeeComments?: string;
  milestones?: string;
  general?: string;
}

function mapValidationErrors(errors: string[]): FieldErrors {
  const fieldErrors: FieldErrors = {};
  for (const err of errors) {
    const lower = err.toLowerCase();
    if (lower.includes('title')) fieldErrors.title = err;
    else if (lower.includes('description')) fieldErrors.description = err;
    else if (lower.includes('measure of success')) fieldErrors.measureOfSuccess = err;
    else if (lower.includes('start date')) fieldErrors.startDate = err;
    else if (lower.includes('end date')) fieldErrors.endDate = err;
    else if (lower.includes('weightage') && !lower.includes('milestone')) fieldErrors.weightage = err;
    else if (lower.includes('milestone')) fieldErrors.milestones = err;
    else if (lower.includes('review cycle')) fieldErrors.general = err;
    else fieldErrors.general = err;
  }
  return fieldErrors;
}

const emptyMilestone = (): InlineMilestone => ({ title: '', description: '', dueDate: '', weightage: '' });

const ObjectiveForm: React.FC<ObjectiveFormProps> = ({
  objective,
  existingWeightageTotal = 0,
  existingMilestones,
  onSave,
  onCancel,
  reviewCycleId = '',
  loading = false,
  defaultManagerId = '',
  defaultManagerName = '',
}) => {
  const [title, setTitle] = useState(objective?.title ?? '');
  const [description, setDescription] = useState(objective?.description ?? '');
  const [measureOfSuccess, setMeasureOfSuccess] = useState(objective?.measureOfSuccess ?? '');
  const [startDate, setStartDate] = useState(objective?.startDate ?? '');
  const [endDate, setEndDate] = useState(objective?.endDate ?? '');
  const [weightage, setWeightage] = useState<string>(
    objective?.weightage !== undefined ? String(objective.weightage) : ''
  );
  const [employeeComments, setEmployeeComments] = useState(objective?.employeeComments ?? '');
  /*
   * The objective's OWN progress. Seeded from the saved objective when editing and 0 on create.
   *
   * Deliberately independent of the milestone rows below: a milestone's progress describes that
   * milestone, and this describes the objective. The two are separate figures in the data model
   * and this form does not derive one from the other.
   */
  const [progressPercentage, setProgressPercentage] = useState<number>(
    clampProgress(objective?.progressPercentage ?? 0)
  );
  const [trackBy, setTrackBy] = useState<TrackBy>(objective?.trackBy ?? TrackBy.PERCENT);
  const [selectedManagerId] = useState(objective?.managerId ?? defaultManagerId);
  /*
   * Preferred over the objective's stored name, not `objective?.managerName ?? …`: an objective
   * saved earlier carries the 'Unassigned' placeholder, and re-saving it through this form would
   * write that placeholder straight back. The freshly resolved directory name wins whenever there
   * is one; the stored value is the fallback for an edit made while the directory is unreadable.
   */
  const [selectedManagerName] = useState(defaultManagerName || objective?.managerName || '');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /* What the save itself reported, as opposed to what validation found before sending it. */
  const [submitError, setSubmitError] = useState<string | null>(null);
  /* Owned here because the save is awaited here; the hosts do not all pass `loading`. */
  const [saving, setSaving] = useState(false);
  const [milestones, setMilestones] = useState<InlineMilestone[]>(() => {
    if (existingMilestones && existingMilestones.length > 0) {
      return existingMilestones.map(m => ({
        title: m.title,
        description: m.description,
        dueDate: m.dueDate,
        // Not `m.weightage ? ... : ''` — 0 is falsy, so a milestone genuinely weighted 0%
        // came back into the edit form as an empty box, indistinguishable from "never set".
        weightage: m.weightage === null || m.weightage === undefined ? '' : String(m.weightage),
      }));
    }
    return [emptyMilestone()];
  });

  const weightageNum = weightage === '' ? 0 : Number(weightage);
  const totalWeightage = existingWeightageTotal + (isNaN(weightageNum) ? 0 : weightageNum);
  const remainingWeightage = 100 - existingWeightageTotal - (isNaN(weightageNum) ? 0 : weightageNum);

  const barClass = useMemo(() => {
    if (totalWeightage > 100) return 'over';
    if (totalWeightage > 85) return 'warning';
    return '';
  }, [totalWeightage]);

  const msWeightageTotal = useMemo(
    () => milestones.reduce((sum, m) => sum + (m.weightage === '' ? 0 : Number(m.weightage)), 0),
    [milestones]
  );

  const addMilestone = () => setMilestones(prev => [...prev, emptyMilestone()]);

  const removeMilestone = (idx: number) => {
    setMilestones(prev => prev.filter((_, i) => i !== idx));
  };

  const updateMilestone = (idx: number, field: keyof InlineMilestone, value: string) => {
    setMilestones(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const buildInput = useCallback((): Partial<CreateObjectiveInput> => ({
    reviewCycleId: objective?.reviewCycleId ?? reviewCycleId,
    title: title.trim(),
    description: description.trim(),
    measureOfSuccess: measureOfSuccess.trim(),
    startDate,
    endDate,
    weightage: weightage === '' ? undefined as unknown as number : Number(weightage),
    employeeComments: employeeComments.trim() || undefined,
    trackBy,
    progressPercentage,
  }), [title, description, measureOfSuccess, startDate, endDate, weightage, employeeComments, trackBy, progressPercentage, reviewCycleId, objective]);

  const buildMilestoneInputs = useCallback((): CreateMilestoneInput[] => {
    return milestones
      .filter(m => m.title.trim() !== '')
      .map(m => ({
        objectiveId: '', // will be set after objective creation
        title: m.title.trim(),
        description: m.description.trim(),
        dueDate: m.dueDate,
        weightage: m.weightage === '' ? 0 : Number(m.weightage),
      }));
  }, [milestones]);

  const validate = useCallback((): CreateObjectiveInput | null => {
    const input = buildInput();
    const result = validateCreateObjectiveInput(input);
    const errors = [...result.errors];

    const w = typeof input.weightage === 'number' ? input.weightage : 0;
    if (result.errors.every(e => !e.toLowerCase().includes('weightage')) && existingWeightageTotal + w > 100) {
      errors.push(`Total weightage would be ${existingWeightageTotal + w}%. Remaining available: ${100 - existingWeightageTotal}%`);
    }

    // Validate milestones
    const filledMs = milestones.filter(m => m.title.trim() !== '');
    if (filledMs.length > 0) {
      const mwTotal = filledMs.reduce((s, m) => s + (m.weightage === '' ? 0 : Number(m.weightage)), 0);
      if (mwTotal !== 100 && mwTotal !== 0) {
        errors.push(`Milestone weightages must sum to 100% (currently ${mwTotal}%)`);
      }
      for (let i = 0; i < filledMs.length; i++) {
        if (!filledMs[i].dueDate) {
          errors.push(`Milestone ${i + 1} requires a due date`);
        }
      }
    }

    if (errors.length > 0) {
      setFieldErrors(mapValidationErrors(errors));
      return null;
    }

    setFieldErrors({});
    return input as CreateObjectiveInput;
  }, [buildInput, existingWeightageTotal, milestones]);

  const handleSave = async () => {
    const input = validate();
    if (!input) return;
    setSubmitError(null);
    setSaving(true);
    try {
      await onSave(input, selectedManagerId, selectedManagerName, buildMilestoneInputs());
    } catch (error: any) {
      /*
       * Stay on the form with everything still typed in. A save that fails silently and
       * navigates away is indistinguishable from one that worked — which is exactly how an
       * objective create that AppSync had rejected outright reached the employee as an empty
       * My Objectives page.
       */
      setSubmitError(error?.message || 'The goal could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const allErrors = Object.values(fieldErrors).filter(Boolean);
  const busy = loading || saving;

  return (
    <div className="pms-obj-form">
      {objective && (objective.status === 'Approved' || objective.status === 'In_Progress') && (
        <div className="pms-form-reapproval-notice" role="alert">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Editing an approved goal will re-submit it for manager approval.
        </div>
      )}
      <div className="pms-form-section">
        <div className="pms-form-section-title">Goal Details</div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="pms-title">Title <span className="required">*</span></label>
          <input id="pms-title" className={`pms-form-input${fieldErrors.title ? ' error' : ''}`} type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Improve customer satisfaction score" disabled={loading} />
          {fieldErrors.title && <span className="pms-form-error">{fieldErrors.title}</span>}
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="pms-description">Description <span className="required">*</span></label>
          <textarea id="pms-description" className={`pms-form-textarea${fieldErrors.description ? ' error' : ''}`} value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the objective in detail" disabled={loading} rows={3} />
          {fieldErrors.description && <span className="pms-form-error">{fieldErrors.description}</span>}
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="pms-measure">Measure of Success <span className="required">*</span></label>
          <textarea id="pms-measure" className={`pms-form-textarea${fieldErrors.measureOfSuccess ? ' error' : ''}`} value={measureOfSuccess} onChange={e => setMeasureOfSuccess(e.target.value)} placeholder="How will success be measured?" disabled={loading} rows={2} />
          {fieldErrors.measureOfSuccess && <span className="pms-form-error">{fieldErrors.measureOfSuccess}</span>}
        </div>

        <div className="pms-form-row">
          <div className="pms-form-group">
            <label className="pms-form-label" htmlFor="pms-start-date">Start Date <span className="required">*</span></label>
            <input id="pms-start-date" className={`pms-form-input${fieldErrors.startDate ? ' error' : ''}`} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} disabled={loading} />
            {fieldErrors.startDate && <span className="pms-form-error">{fieldErrors.startDate}</span>}
          </div>
          <div className="pms-form-group">
            <label className="pms-form-label" htmlFor="pms-end-date">End Date <span className="required">*</span></label>
            <input id="pms-end-date" className={`pms-form-input${fieldErrors.endDate ? ' error' : ''}`} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={loading} />
            {fieldErrors.endDate && <span className="pms-form-error">{fieldErrors.endDate}</span>}
          </div>
        </div>

        <div className="pms-form-row">
          <div className="pms-form-group">
            <label className="pms-form-label" htmlFor="pms-weightage">Goal Weightage (%) <span className="required">*</span></label>
            <input id="pms-weightage" className={`pms-form-input${fieldErrors.weightage ? ' error' : ''}`} type="number" min={0} max={100} value={weightage} onChange={e => setWeightage(e.target.value)} placeholder="e.g. 25" disabled={loading} />
            {fieldErrors.weightage && <span className="pms-form-error">{fieldErrors.weightage}</span>}
          </div>
          <div className="pms-form-group pms-form-group--remaining">
            <span className="pms-form-label">Remaining</span>
            <span className={`pms-weightage-chip ${barClass}`}>{remainingWeightage}%</span>
          </div>
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="pms-track-by">Track By</label>
          <select id="pms-track-by" className="pms-form-input" value={trackBy} onChange={e => setTrackBy(e.target.value as TrackBy)} disabled={loading}>
            {Object.values(TrackBy).map(tb => (
              <option key={tb} value={tb}>{TRACK_BY_LABELS[tb]}</option>
            ))}
          </select>
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="pms-comments">Employee Comments</label>
          <textarea id="pms-comments" className="pms-form-textarea" value={employeeComments} onChange={e => setEmployeeComments(e.target.value)} placeholder="Any additional comments (optional)" disabled={loading} rows={2} />
        </div>

        {/*
          Objective progress. Labelled "Objective Progress" rather than just "Progress" because
          the milestone rows below each carry their own progress figure, and the whole point is
          that these are different numbers.
        */}
        <div className="pms-form-group">
          <span className="pms-form-label" id="pms-objective-progress-label">Objective Progress</span>
          <ProgressSlider
            value={progressPercentage}
            onChange={setProgressPercentage}
            disabled={loading}
            showMarks
            ariaLabel="Objective progress"
          />
          <span className="pms-form-hint">
            The objective's own completion. Milestone progress is tracked separately on each
            milestone below.
          </span>
        </div>
      </div>

      {/* Milestones Section */}
      <div className="pms-form-section">
        <div className="pms-form-section-header">
          <div className="pms-form-section-title">Milestones (Measures of Success)</div>
          <button type="button" className="pms-ms-add-btn" onClick={addMilestone} disabled={loading} title="Add milestone">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            Add
          </button>
        </div>

        {milestones.length === 0 && (
          <div className="pms-ms-empty">
            No milestones added. Click + Add to define measures of success.
          </div>
        )}

        {milestones.map((ms, idx) => (
          <div key={idx} className="pms-ms-card">
            <div className="pms-ms-card-header">
              <span className="pms-ms-card-num">#{idx + 1}</span>
              {milestones.length > 1 && (
                <button type="button" className="pms-ms-remove-btn" onClick={() => removeMilestone(idx)} disabled={loading} title="Remove milestone">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
            <div className="pms-form-group">
              <label className="pms-form-label">Milestone Title <span className="required">*</span></label>
              <input className="pms-form-input" type="text" value={ms.title} onChange={e => updateMilestone(idx, 'title', e.target.value)} placeholder="e.g. Complete user research" disabled={loading} />
            </div>
            <div className="pms-form-group">
              <label className="pms-form-label">Description</label>
              <input className="pms-form-input" type="text" value={ms.description} onChange={e => updateMilestone(idx, 'description', e.target.value)} placeholder="Brief description" disabled={loading} />
            </div>
            <div className="pms-form-row">
              <div className="pms-form-group">
                <label className="pms-form-label">Due Date <span className="required">*</span></label>
                <input className="pms-form-input" type="date" value={ms.dueDate} onChange={e => updateMilestone(idx, 'dueDate', e.target.value)} disabled={loading} />
              </div>
              <div className="pms-form-group">
                <label className="pms-form-label">Weightage (%)</label>
                <input className="pms-form-input" type="number" min={0} max={100} value={ms.weightage} onChange={e => updateMilestone(idx, 'weightage', e.target.value)} placeholder="e.g. 30" disabled={loading} />
              </div>
            </div>
          </div>
        ))}

        {milestones.filter(m => m.title.trim()).length > 0 && (
          <div className="pms-ms-weightage-summary">
            <span>Milestone Weightage Total:</span>
            <span className={msWeightageTotal === 100 ? 'pms-ms-wt-ok' : msWeightageTotal > 0 ? 'pms-ms-wt-warn' : ''}>
              {msWeightageTotal}/100
            </span>
          </div>
        )}
        {fieldErrors.milestones && <span className="pms-form-error">{fieldErrors.milestones}</span>}
      </div>

      {/* Weightage allocation bar */}
      <div className="pms-weightage-bar">
        <div className="pms-weightage-bar__header">
          <span>Total Goal Weightage Allocation</span>
          <span>{Math.min(totalWeightage, 100)}% / 100%</span>
        </div>
        <div className="pms-weightage-bar__track" role="progressbar" aria-valuenow={Math.min(totalWeightage, 100)} aria-valuemin={0} aria-valuemax={100}>
          <div className={`pms-weightage-bar__fill ${barClass}`} style={{ width: `${Math.min(totalWeightage, 100)}%` }} />
        </div>
      </div>

      {allErrors.length > 0 && (
        <div className="pms-form-error-summary" role="alert">
          <strong>Please fix the following:</strong>
          <ul>{allErrors.map((err, i) => <li key={i}>{err}</li>)}</ul>
        </div>
      )}

      {/* Separate from the validation summary: this is not something the employee can fix. */}
      {submitError && (
        <div className="pms-form-error-summary" role="alert">
          <strong>The goal was not saved.</strong>
          <p>{submitError}</p>
        </div>
      )}

      <div className="pms-form-actions">
        <button type="button" className="pms-btn pms-btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="pms-btn pms-btn-primary" onClick={handleSave} disabled={busy}>{busy ? 'Saving…' : objective ? 'Update Goal' : 'Save Goal'}</button>
      </div>
    </div>
  );
};

export default ObjectiveForm;
